import React, { useState, useMemo, useContext, useRef } from 'react';
import { getFirestore, getDoc, getDocs, collection, setDoc, doc, Timestamp } from 'firebase/firestore';
import { AppContext } from './AppContext';

const SongItemMusicVideo = ({ item, onPress }) => {
  const { userId2 } = useContext(AppContext);
  const firestore = getFirestore();

  const artworkUrl = item?.ArtworkURL || item?.artworkURL;
  const isDat = !!(item?.isNFT || item?.editions || item?.tier);

  // ───────── helpers: timestamp type detection / normalization ─────────
  const toMillis = (v) =>
    v == null
      ? Date.now()
      : typeof v === 'number'
      ? v
      : typeof v === 'string'
      ? (Date.parse(v) || Date.now())
      : (v?.seconds != null)
      ? v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6)
      : typeof v?.toMillis === 'function'
      ? v.toMillis()
      : v instanceof Date
      ? v.getTime()
      : typeof v?.toDate === 'function'
      ? v.toDate().getTime()
      : Date.now();

  const detectType = (v) =>
    v?.seconds != null
      ? 'fs'
      : typeof v === 'number'
      ? 'num'
      : typeof v === 'string'
      ? 'str'
      : v instanceof Date
      ? 'date'
      : typeof v?.toDate === 'function' || typeof v?.toMillis === 'function'
      ? 'fs'
      : 'unknown';

  const originalTsType = detectType(item?.releaseTimestamp);
  const localDate = item?.releaseTimestamp ? new Date(toMillis(item.releaseTimestamp)) : new Date();

  // UI state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [date, setDate] = useState(localDate.toISOString().split('T')[0]); // YYYY-MM-DD
  const dateInputRef = useRef(null);

  const mandatoryFollowingIds = [
    'h0fEIStkM4XHTDErZM5i8XIo9rA3',
    'OHaY20b54nTGd5BBgbJOL2BHEbX2',
    'rdX289s3YBP31Hw5qn2eXVywtBq1',
    'Mkf4631LCEOYmHXWRzGirPdxBrt1'
  ];

  // Keep native date-picker-style behavior (click to pick) but allow 4-digit year typing
  const handleManualDateInput = (e) => {
    const v = e.target.value;
    // Allow progressive typing: YYYY, YYYY-, YYYY-MM, YYYY-MM-, YYYY-MM-DD
    if (/^\d{0,4}(-\d{0,2}){0,2}$/.test(v)) {
      setDate(v);
    }
  };

  const finalizeIfYearOnly = () => {
    if (/^\d{4}$/.test(date)) {
      const current = new Date();
      setDate(
        `${date}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(
          current.getDate()
        ).padStart(2, '0')}`
      );
    }
  };

  const handleConfirm = async (e) => {
    e.stopPropagation();

    // Build updated date using chosen calendar date but PRESERVE original time-of-day
    const updatedDate = new Date(date);
    updatedDate.setUTCHours(
      localDate.getUTCHours(),
      localDate.getUTCMinutes(),
      localDate.getUTCSeconds(),
      localDate.getUTCMilliseconds()
    );

    // Preserve existing type for sorting consistency
    const newSummaryValue =
      originalTsType === 'fs'
        ? Timestamp.fromDate(updatedDate)
        : originalTsType === 'num'
        ? updatedDate.getTime()
        : originalTsType === 'str'
        ? updatedDate.toISOString()
        : Timestamp.fromDate(updatedDate);

    try {
      // Music Video items live under musicVideoSummaries/* in a 'videos' array
      const mvSnaps = await getDocs(collection(firestore, 'musicVideoSummaries'));
      const videoId = item.contentId || item.id;

      for (const d of mvSnaps.docs) {
        const data = d.data() || {};
        const videos = Array.isArray(data.videos) ? data.videos : [];

        if (videos.some(v => (v.id || v.contentId) === videoId)) {
          const next = videos.map(v =>
            (v.id === videoId || v.contentId === videoId)
              ? { ...v, releaseTimestamp: newSummaryValue }
              : v
          );
          await setDoc(d.ref, { videos: next }, { merge: true });
          break;
        }
      }

      // Update local UI state
      setDate(updatedDate.toISOString().split('T')[0]);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating music video releaseTimestamp:', err);
    }
  };

  // ───────── rendering helpers ─────────
  const formatArtistFeatures = (artistFeatures) => {
    return artistFeatures
      .map((feature) => {
        if (/^\d+$/.test(feature.id)) {
          return feature.tag;
        } else {
          const parts = feature.tag.split(' - ');
          return parts.length > 1 ? parts[1] : parts[0];
        }
      })
      .join(', ');
  };

  const songDisplayName = useMemo(() => {
    return item.isAlbum
      ? item.albumName || 'Unknown Album'
      : `${item.SongName || 'Unknown Song'}${
          item.artistFeatures && item.artistFeatures.length > 0
            ? ` ft. ${formatArtistFeatures(item.artistFeatures)}`
            : ''
        }`;
  }, [item]);

  if (!item) {
    return <div style={styles.videoThumbnailPlaceholder}>Loading...</div>;
  }

  return (
    <div style={styles.videoItemContainer} onClick={() => onPress(item)}>
      <div style={styles.horizontalTouchable}>
        {artworkUrl ? (
          <img src={artworkUrl} alt="Artwork" style={styles.videoThumbnail} />
        ) : (
          <div style={styles.videoThumbnailPlaceholder}>Loading...</div>
        )}
      </div>

      <div style={styles.artistName}>{item.ArtistName}</div>
      <div style={styles.songName}>{songDisplayName}</div>

      {mandatoryFollowingIds.includes(userId2) && (
        <div style={styles.timestampContainer} onClick={(e) => e.stopPropagation()}>
          {!isEditing ? (
            <>
              <div style={styles.timestampText}>
                Release Date: {new Date(toMillis(item?.releaseTimestamp)).toLocaleDateString()}
              </div>
              <button
                style={styles.editButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                Edit
              </button>
            </>
          ) : (
            <div style={styles.editContainer}>
              <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={date}
                ref={dateInputRef}
                onChange={handleManualDateInput}
                onBlur={finalizeIfYearOnly}
                style={styles.dateInput}
                onClick={(e) => e.stopPropagation()}
              />
              <button style={styles.confirmButton} onClick={handleConfirm}>
                Confirm
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  videoItemContainer: {
    width: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginRight: '10px',
    cursor: 'pointer',
  },
  horizontalTouchable: {
    width: '200px',
    height: '112.5px', // 16:9
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  videoThumbnail: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  videoThumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    backgroundColor: '#cccccc',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  artistName: {
    marginTop: '10px',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    textAlign: 'left',
    fontFamily: 'brhendrix',
  },
  songName: {
    marginTop: '2px',
    color: '#b8b8b8',
    fontFamily: 'brhendrix',
    fontSize: '14px',
    whiteSpace: 'normal',
    textAlign: 'left',
  },
  timestampContainer: {
    marginTop: '8px',
    color: '#b8b8b8',
    fontFamily: 'brhendrix',
    fontSize: '10px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
  },
  timestampText: {
    marginBottom: '6px',
  },
  editButton: {
    backgroundColor: '#62fbfa',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '4px',
    alignSelf: 'flex-start',
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  dateInput: {
    fontSize: '14px',
    padding: '6px',
    borderRadius: '4px',
  },
  confirmButton: {
    backgroundColor: '#62fbfa',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '4px',
    alignSelf: 'flex-start',
  },
};

export default React.memo(SongItemMusicVideo);
